import { useState } from "react";
import axios from "axios";
import useTranslation from "@hooks/use_translation";
import { useShowFlash } from "@sprout/flash.tsx";
import { Body, SectionTitle } from "@sprout/typography";
import { Row, Stack } from "@sprout/layout";
import { SecondaryButton, DestructiveButton, TertiaryDangerButton } from "@sprout/buttons";
import GoogleLogo from "@images/google_logo.svg";
import AppleLogo from "@images/apple_logo.svg";
import LinkedinLogo from "@images/linkedin_logo.svg";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

export type IdentityProvider = {
  provider: string;
  connected: boolean;
};

type Props = {
  identities: IdentityProvider[];
  identitiesPath: string;
};

const PROVIDER_LOGOS: Record<string, string> = {
  google_oauth2: GoogleLogo,
  apple: AppleLogo,
  linkedin: LinkedinLogo,
};

const ConnectedAccountsCard = ({ identities: initialIdentities, identitiesPath }: Props) => {
  const { t } = useTranslation("user");
  const showFlash = useShowFlash();
  const [identities, setIdentities] = useState(initialIdentities);
  const [providerToDeactivate, setProviderToDeactivate] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const connectedIdentities = identities.filter((identity) => identity.connected);

  if (connectedIdentities.length === 0) {
    return null;
  }

  const disconnect = async (provider: string) => {
    setDisconnecting(provider);

    try {
      await axios.delete(identitiesPath.replace(":provider", provider));
      setIdentities((current) =>
        current.map((identity) =>
          identity.provider === provider ? { ...identity, connected: false } : identity,
        ),
      );
      showFlash(
        t("connectedAccounts.disconnected", {
          provider: t(`connectedAccounts.providers.${provider}`),
        }),
        "success",
      );
    } catch (_e) {
      showFlash(t("connectedAccounts.error"), "error");
    } finally {
      setDisconnecting(null);
      setDialogOpen(false);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-6 w-full">
      <Stack gap="16px" className="w-full">
        <SectionTitle medium>{t("connectedAccounts.title")}</SectionTitle>
        {connectedIdentities.map(({ provider }) => {
          const providerLabel = t(`connectedAccounts.providers.${provider}`);

          return (
            <Row key={provider} align="center" gap="12px" className="w-full justify-between">
              <Stack>
                <Row gap="8px" align="center">
                  <img src={PROVIDER_LOGOS[provider]} alt="" className="h-5 w-5" />
                  <Body medium>{providerLabel}</Body>
                </Row>
                <span className="px-3 py-1 rounded-lg text-[14px] leading-5 w-fit bg-green-e50">
                  {t("connectedAccounts.connected")}
                </span>
              </Stack>
              <TertiaryDangerButton
                onClick={() => {
                  setProviderToDeactivate(provider);
                  setDialogOpen(true);
                }}
                disabled={disconnecting === provider}
              >
                {t("connectedAccounts.disconnect")}
              </TertiaryDangerButton>
            </Row>
          );
        })}
      </Stack>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              <SectionTitle medium>
                {t("connectedAccounts.modal.title", {
                  provider:
                    providerToDeactivate &&
                    t(`connectedAccounts.providers.${providerToDeactivate}`),
                })}
              </SectionTitle>
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            <Body>
              {t("connectedAccounts.modal.body", {
                provider:
                  providerToDeactivate && t(`connectedAccounts.providers.${providerToDeactivate}`),
              })}
            </Body>
          </DialogDescription>
          <DialogFooter>
            <DialogClose>
              <SecondaryButton>{t("connectedAccounts.modal.cancel")}</SecondaryButton>
            </DialogClose>
            <DestructiveButton
              loading={disconnecting === providerToDeactivate}
              onClick={() => providerToDeactivate && disconnect(providerToDeactivate)}
            >
              {t("connectedAccounts.modal.confirm")}
            </DestructiveButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectedAccountsCard;
